import { useEffect, useState, useRef } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { AttachmentProps } from '../models/attachment'
import { ResourcesService } from '../services/ResourcesService'
import { FILTERS_REDUCER_NAMESPACE } from '../redux/reducers/filters'
import Scheduling from '../components/NewResource/Scheduling/Scheduling'
import Information from '../components/NewResource/Information/Information'
import { NEW_RESOURCE_REDUCER_NAMESPACE } from '../redux/reducers/new-resource'
import Distribution from '../components/ActivityCreation/Distribution/DistributionList'
import ReferenceFolder from '../components/NewResource/ReferenceFolder/ReferenceFolder'
import ArticleChapters from '../components/NewResource/ArticleChapters/ArticleChapters'
import {
    Analytics,
    canCreate,
    layoutActions,
    SmartlyActionItems,
} from '@Techedge/digitalportal-shared'
import { FiltersService } from '@/app/services/FiltersService'
import { getDepartments, getFiltersDomain, getVisibilities } from '../redux/actions/filters'
import {
    Color,
    LuxAccordion,
    LuxButton,
    LuxModalFullScreen,
    LuxMultiSelect,
    LuxSelect,
    LuxTextInput,
} from '@lux/wardrobe-lib'
import {
    createDocument,
    createFolder,
    editDocument,
    editFolder,
    getDocumentDetails,
    getDocuments,
    getForms,
    publishArticle,
    setSmartFilterResources,
} from '../redux/actions/new-resource'
import {
    AccordionTabProps,
    Chapter,
    DistributionListProps,
    FolderType,
    FormProps,
    ResourceProps,
    ResourceType,
    SurveyProps,
    TreeFolderProps,
    Visibility,
} from '../models/new-resource'
import './NewResource.scss'
import DialogPropagation from '@components/UI/Dialogs/DialogStoreList/DialogPropagation'
import { MenuItem } from 'primereact/menuitem'

const NewResource = (props: {
    accessToken: string
    documents: ResourceProps[]
    forms: ResourceProps[]
    smartFilter?: any
    loading: boolean
    loadingFolders: boolean
    resourceId: string
    departmentList: any[]
    visibilityList: any[]
    externalResourceType: ResourceType
    externalReferenceFolderId: string
    externalReferenceFolderTitle: string
    isOpenDocument: boolean
    isFormSection: boolean
    documentsApiFullfilled: boolean
    getDocuments: (filters: { folderType: FolderType }) => void
    getForms: (filters: { folderType: FolderType }) => void
    getDocumentDetails: (documentId: string, callback: (data: ResourceProps) => void) => void
    createFolder: (body: {
        title: string
        folderType: string
        smartFilter?: any
        visibility: Visibility[]
        storeList: string[]
        parentFolderId?: string
    }) => void
    editFolder: (body: {
        title: string
        visibility: Visibility[]
        storeList: string[]
        folderId: string
        smartFilter?: any
        parentFolderId?: string
        force?: boolean
    }) => void
    createDocument: (
        body: {
            title?: string
            resourceType: string
            department: string
            attachments?: AttachmentProps[]
            attachmentData?: { _id: string; title: string }[]
            parentFolderId?: string
            recipients?: string[]
            survey?: SurveyProps
            abstract?: any
            chapters?: Chapter[]
            requiredConfirmReading?: boolean
            enableCommentsAndReactions?: boolean
            storeList: string[]
            visibility: Visibility[]
        },
        publishAfterSave?: boolean
    ) => void
    editDocument: (
        _id: string,
        body: {
            title: string
            department: string
            attachments?: AttachmentProps[]
            parentFolderId?: string
            recipients?: string[]
            survey?: SurveyProps
            abstract?: any
            chapters?: Chapter[]
            resetViewsCount?: boolean
            requiredConfirmReading?: boolean
            enableCommentsAndReactions?: boolean
            storeList: string[]
            visibility: Visibility[]
        },
        publishAfterSave?: boolean
    ) => void
    onClose: () => void
    getDepartments: () => void
    getVisibilities: () => void
    publishArticle: (documentId: string) => void
    setFullLoading: (fullLoading: boolean) => void
    setSmartFilter: (value: any) => void
    getFiltersDomain: () => void
}) => {
    const {
        accessToken,
        documents,
        forms,
        loading,
        loadingFolders,
        resourceId,
        departmentList,
        visibilityList,
        externalResourceType,
        externalReferenceFolderId,
        externalReferenceFolderTitle,
        isOpenDocument,
        smartFilter,
        isFormSection,
        documentsApiFullfilled,
        getDocuments,
        getForms,
        getDocumentDetails,
        createFolder,
        editFolder,
        createDocument,
        editDocument,
        onClose,
        getDepartments,
        getVisibilities,
        setFullLoading,
        setSmartFilter,
        getFiltersDomain,
    } = props

    const intl = useIntl()

    const componentClass = 'smart-modals-new-resource'

    const [isEdit, setIsEdit] = useState<boolean>(false)
    const [folders, setFolders] = useState<ResourceProps[]>([])
    const [referenceFolderId, setReferenceFolderId] = useState<string>('')
    const [resourceType, setResourceType] = useState<ResourceType>(
        !externalResourceType && isOpenDocument ? ResourceType.file : ResourceType.folder
    )
    const [referenceFolderObject, setReferenceFolderObject] = useState<ResourceProps>({
        _id: '',
        title: '',
    })
    const [referenceSpace, setReferenceSpace] = useState<FolderType>(
        isFormSection ? FolderType.form : FolderType.document
    )
    const [folderTitle, setFolderTitle] = useState<string>('')
    const [folderType, setFolderType] = useState<string>('')
    const [articleTitle, setArticleTitle] = useState<string>('')
    const [formTitle, setFormTitle] = useState<string>('')
    const [department, setDepartment] = useState<string>('')
    const [recipients, setRecipients] = useState<string[]>([])
    const [visibility, setVisibility] = useState<Visibility[]>([])
    const [startDate, setStartDate] = useState<Date>(null)
    const [endDate, setEndDate] = useState<Date>(null)
    const [priority, setPriority] = useState<boolean>(false)
    const [selectedForms, setSelectedForms] = useState<FormProps[]>([])
    const [saveDisabled, setSaveDisabled] = useState<boolean>(true)
    const [showDialogPropagation, setShowDialogPropagation] = useState<boolean>(false)
    const [subFolderStoreList, setSubFolderStoreList] = useState<any[]>([])
    const [abstract, setAbstract] = useState<any>('')
    const [chaptersList, setChaptersList] = useState<Chapter[]>([])
    const [attachments, setAttachments] = useState<AttachmentProps[]>([])
    const [resetViewsCount, setResetViewsCount] = useState<boolean>(false)
    const [requiredConfirmReading, setRequiredConfirmReading] = useState<boolean>(false)
    const [enableCommentsAndReactions, setEnableCommentsAndReactions] = useState<boolean>(false)
    const [showResetViewsCount, setShowResetViewsCount] = useState<boolean>(true)
    const [originalStoreList, setOriginalStoreList] = useState<any[]>([])
    const [matchingTable, setMatchingTable] = useState<any>({ add: false, remove: false })
    const [originalSmartFilters] = useState(smartFilter || {})

    const [status, setStatus] = useState<string>('no-changes')

    const [propagate, setPropagate] = useState<boolean>(false)

    const [folderDetails, setFolderDetails] = useState<ResourceProps>({
        _id: '',
        title: '',
    })
    const [distributionList, setDistributionList] = useState<DistributionListProps>({
        listName: {
            value: '',
        },
        selectedStoreList: {
            value: [],
            mandatory: true,
        },
        selectedDistributionList: {
            value: [],
        },
    })
    const [customStoreList, setCustomStoreList] = useState(null)

    const [reloadParentFolder, setReloadParentFolder] = useState<{
        flag: boolean
        parentFolderId: string | null
        isInit: boolean
    }>({ flag: false, parentFolderId: null, isInit: true })

    const currentRequestRef = useRef<string | null>(null)

    useEffect(() => {
        setReferenceFolderId(referenceFolderObject._id || referenceFolderObject.id)
    }, [referenceFolderObject])

    useEffect(() => {
        if (referenceFolderId && !externalReferenceFolderTitle) {
            const requestId = referenceFolderId
            currentRequestRef.current = requestId

            ResourcesService.GetFolder({
                folderId: referenceFolderId,
            }).then((response: any) => {
                if (currentRequestRef.current === requestId) {
                    setCustomStoreList(response.data?.storeList)
                    setReferenceFolderObject({
                        ...referenceFolderObject,
                        ...(response.data || {}),
                    })
                }
            })
        }

        return () => {
            currentRequestRef.current = null
        }
    }, [referenceFolderId])

    useEffect(() => {
        setOriginalStoreList(distributionList.selectedStoreList.value)
    }, [distributionList])

    useEffect(() => {
        if (status == 'no-changes') {
            setPropagate(false)
        } else {
            setPropagate(true)
        }
    }, [status])

    useEffect(() => {
        if (!resourceId) {
            if (folderDetails?.folderType === FolderType.form) {
                Analytics._sendAnalyticsLinkEvent('Resources', 'Forms:Creation', 'Static')
            } else {
                Analytics._sendAnalyticsLinkEvent('Resources', 'Documents:Creation', 'Static')
            }
        }
    }, [])

    useEffect(() => {
        if (accessToken) {
            setFullLoading(true)
            getDocuments({ folderType: FolderType.document })
            getForms({ folderType: FolderType.form })
            getDepartments()
            getVisibilities()
            getFiltersDomain()
        }
    }, [accessToken])

    useEffect(() => {
        if (!isEdit && documentsApiFullfilled) setFullLoading(false)
    }, [documentsApiFullfilled, isEdit])

    useEffect(() => {
        if (!isEdit) {
            setReferenceSpace(isFormSection ? FolderType.form : FolderType.document)
            setReferenceFolderId('')
            setFolderTitle('')
            setArticleTitle('')
            setFormTitle('')
            setDepartment('')
            setAbstract('')
            setRecipients([])
            setVisibility([])
            setStartDate(null)
            setEndDate(null)
            setPriority(false)
            setSelectedForms([])
            setSaveDisabled(true)
            setCustomStoreList(null)
            setAttachments([])
            setChaptersList([])
            setResetViewsCount(false)
            setRequiredConfirmReading(false)
            setEnableCommentsAndReactions(false)
            setFolderDetails({
                _id: '',
                title: '',
            })
            setDistributionList({
                listName: {
                    value: '',
                },
                selectedStoreList: {
                    value: [],
                    mandatory: true,
                },
                selectedDistributionList: {
                    value: [],
                },
            })
            switch (resourceType) {
                case ResourceType.file:
                case ResourceType.article:
                    setFolderType(FolderType.document)
                    break
                case ResourceType.form:
                    setFolderType(FolderType.form)
                    break
                default:
                    setFolderType(ResourceType.document)
                    break
            }
        }
    }, [resourceType])

    useEffect(() => {
        switch (resourceType) {
            case ResourceType.folder:
                setSaveDisabled(
                    folderTitle === '' ||
                        !visibility.length ||
                        !distributionList.selectedStoreList.value.length
                )
                break
            case ResourceType.subfolder:
                setSaveDisabled(
                    referenceFolderObject?.title === '' ||
                        folderTitle === '' ||
                        !visibility.length ||
                        !distributionList.selectedStoreList.value.length
                )
                break
            case ResourceType.file:
                setSaveDisabled(
                    referenceFolderObject?.title === '' ||
                        department === '' ||
                        !attachments ||
                        attachments.length === 0 ||
                        visibility.length === 0 ||
                        !distributionList.selectedStoreList.value.length
                )
                break
            case ResourceType.article:
                setSaveDisabled(
                    referenceFolderObject?.title === '' || articleTitle === '' || department === ''
                )
                break
            case ResourceType.form:
                setSaveDisabled(
                    referenceFolderObject?.title === '' ||
                        formTitle === '' ||
                        recipients?.length === 0 ||
                        department === '' ||
                        startDate === null ||
                        endDate === null ||
                        selectedForms?.length === 0
                )
                break
            default:
                setSaveDisabled(true)
                break
        }
    }, [
        referenceFolderObject,
        folderTitle,
        folderType,
        articleTitle,
        formTitle,
        department,
        recipients,
        visibility,
        startDate,
        endDate,
        priority,
        selectedForms,
        distributionList,
        abstract,
        attachments,
        chaptersList,
    ])

    const onChangeParentFolder = (folder) => {
        setVisibility([])
        setSubFolderStoreList([])
        setFolderDetails((old) => ({
            ...old,
            parentFolderId: folder._id,
            storeList: [],
        }))

        setDistributionList({
            ...distributionList,
            selectedStoreList: {
                ...distributionList.selectedStoreList,
                value: [],
            },
        })

        setReloadParentFolder((old) => ({
            ...old,
            flag: true,
            parentFolderId: folder._id,
        }))
    }

    const setSubfolderDistributionList = (folderDetails: ResourceProps) => {
        if (folderDetails?.storeList) {
            const newSubFolderStoreList =
                subFolderStoreList.length > 0
                    ? subFolderStoreList?.map((s) => ({ store_id: s }))
                    : folderDetails?.storeList.map((s) => ({ store_id: s }))
            setDistributionList({
                ...distributionList,
                selectedStoreList: {
                    ...distributionList.selectedStoreList,
                    value: newSubFolderStoreList ?? [],
                },
            })
            if (folderDetails.parentFolderId) {
                setCustomStoreList(referenceFolderObject?.storeList)
            } else {
                setCustomStoreList(folderDetails?.storeList)
            }
        } else {
            setCustomStoreList(null)
            setDistributionList({
                listName: {
                    value: '',
                },
                selectedStoreList: {
                    value: [],
                    mandatory: true,
                },
                selectedDistributionList: {
                    value: [],
                },
            })
        }
    }

    useEffect(() => {
        if (!!folderDetails?._id) {
            if (folderDetails?.folderType === FolderType.form) {
                if (isEdit) {
                    Analytics._sendAnalyticsLinkEvent('Resources', 'Forms:Detail:Edit', 'Static')
                }
            } else {
                if (isEdit) {
                    Analytics._sendAnalyticsLinkEvent(
                        'Resources',
                        'Documents:Detail:Edit',
                        'Static'
                    )
                }
            }
            if (resourceType === ResourceType.folder) {
                setFolderTitle(folderDetails?.title)
                setFolderType(folderDetails?.folderType)
                setVisibility(folderDetails?.visibility)
                setFullLoading(true)
                FiltersService.GetStoreList({
                    mode: 'standard',
                    coneOfVision: true,
                    groupedFields: 'all',
                }).then((res: any) => {
                    if (res?.data) {
                        let _storeList = []
                        folderDetails.storeList.forEach((store) => {
                            let found = res.data.stores.find((x) => x.store_id == store)
                            if (found) {
                                _storeList.push(store)
                            }
                        })
                        setDistributionList({
                            ...distributionList,
                            selectedStoreList: {
                                ...distributionList.selectedStoreList,
                                value: _storeList.map((s) => ({ store_id: s })),
                            },
                        })
                    }
                    setFullLoading(false)
                })
                return
            } else if (resourceType === ResourceType.subfolder) {
                if (isEdit && !referenceFolderObject?._id) {
                    setFolderTitle(folderDetails?.title)
                    setFolderType(folderDetails?.folderType)
                    setSubFolderStoreList(folderDetails?.storeList)
                    setVisibility(folderDetails?.visibility)
                    setDistributionList({
                        ...distributionList,
                        selectedStoreList: {
                            ...distributionList.selectedStoreList,
                            value: folderDetails.storeList?.length
                                ? folderDetails.storeList.map((s) => ({ store_id: s }))
                                : [],
                        },
                    })
                    setFullLoading(true)
                    FiltersService.GetStoreList({
                        mode: 'standard',
                        coneOfVision: true,
                        groupedFields: 'all',
                    }).then((res: any) => {
                        if (res?.data) {
                            ResourcesService.GetFolder({
                                folderId: folderDetails.parentFolderId,
                            })
                                .then((response: any) => {
                                    if (response && response.data) {
                                        let _storeList = []
                                        response.data.storeList.forEach((store) => {
                                            let found = res.data.stores.find(
                                                (x) => x.store_id == store
                                            )
                                            if (found) {
                                                _storeList.push(store)
                                            }
                                        })
                                        setCustomStoreList(response.data.storeList)
                                        setReferenceFolderObject({
                                            _id: folderDetails?.parentFolderId,
                                            title: externalReferenceFolderTitle,
                                            folderType: folderDetails?.folderType,
                                            storeList: _storeList,
                                        })
                                    }
                                })
                                .finally(() => setFullLoading(false))
                        }
                    })
                } else if (isEdit && reloadParentFolder.flag && reloadParentFolder.parentFolderId) {
                    setFullLoading(true)
                    FiltersService.GetStoreList({
                        mode: 'standard',
                        coneOfVision: true,
                        groupedFields: 'all',
                    }).then((res: any) => {
                        if (res?.data) {
                            ResourcesService.GetFolder({
                                folderId: reloadParentFolder.parentFolderId,
                            })
                                .then((response: any) => {
                                    if (response && response.data) {
                                        let _storeList = []
                                        response.data.storeList.forEach((store) => {
                                            let found = res.data.stores.find(
                                                (x) => x.store_id == store
                                            )
                                            if (found) {
                                                _storeList.push(store)
                                            }
                                        })
                                        setCustomStoreList(response.data.storeList)
                                    }
                                })
                                .finally(() => {
                                    setReloadParentFolder((old) => ({
                                        ...old,
                                        flag: false,
                                        isInit: false,
                                    }))
                                })
                        }
                        setFullLoading(false)
                    })
                } else {
                    setFolderType(folderDetails?.folderType)
                    setVisibility((value) => (!value?.length ? folderDetails?.visibility : value))
                    setSubfolderDistributionList(folderDetails)
                }
                return
            } else {
                setVisibility(folderDetails?.visibility)
                setFolderType(folderDetails?.folderType)
                setSubfolderDistributionList(folderDetails)
                return
            }
        }
    }, [folderDetails, reloadParentFolder])

    useEffect(() => {
        if (resourceType && documents && forms && referenceSpace) {
            setFolders([])
            switch (resourceType) {
                case ResourceType.folder:
                    setFolders([])
                    break
                case ResourceType.subfolder:
                    switch (referenceSpace) {
                        case FolderType.document:
                            setFolders([...documents])
                            break
                        case FolderType.form:
                            setFolders([...forms])
                            break
                        default:
                            setFolders([])
                            break
                    }
                    break
                case ResourceType.file:
                    setFolders([...documents])
                    break
                case ResourceType.article:
                    setFolders([...documents])
                    break
                case ResourceType.form:
                    setFolders([...forms])
                    break
                default:
                    setFolders([])
                    break
            }
        }
    }, [resourceType, documents, forms, referenceSpace])

    useEffect(() => {
        if (accessToken) {
            setIsEdit(false)
            if (externalResourceType) {
                setResourceType(externalResourceType)
                if (
                    !isEdit &&
                    canCreate(SmartlyActionItems.FORM) &&
                    !canCreate(SmartlyActionItems.DOCUMENT) &&
                    (externalResourceType === ResourceType.file ||
                        externalResourceType === ResourceType.article)
                ) {
                    setResourceType(ResourceType.form)
                }
                if (!!resourceId) {
                    setIsEdit(true)
                    switch (externalResourceType) {
                        case ResourceType.folder:
                        case ResourceType.subfolder:
                            ResourcesService.GetFolder({
                                folderId: resourceId,
                            }).then((res: any) => {
                                if (res && res.data) {
                                    setFolderDetails(res.data)
                                    setSmartFilter(res.data.smartFilter)
                                }
                            })
                            break
                        case ResourceType.file:
                            getDocumentDetails(resourceId, (data: ResourceProps) => {
                                setVisibility(data?.visibility || [])
                                setDepartment(data?.department || '')
                                setCustomStoreList(data?.parentFolder.storeList)
                                setReferenceFolderObject(data?.parentFolder)
                                if (data?.attachments?.length > 0) {
                                    setAttachments(
                                        data.attachments.map((file: any) => ({
                                            ...file,
                                            fileUrl: file.url,
                                            fileName: `${file.name}.${file.ext}`,
                                            fileExtension: file.ext,
                                        }))
                                    )
                                }
                            })
                            break
                        case ResourceType.article:
                            getDocumentDetails(resourceId, (data: ResourceProps) => {
                                setArticleTitle(data?.title || '')
                                setDepartment(data?.department || '')
                                setAbstract(data?.abstract || '')
                                setVisibility(data?.visibility || [])

                                if (data?.attachments?.length > 0) {
                                    let tmpAttachments = data.attachments
                                    setAttachments(
                                        tmpAttachments.map((tmpAttachment: AttachmentProps) => {
                                            return {
                                                ...tmpAttachment,
                                                fileUrl: tmpAttachment.url,
                                                fileName: `${tmpAttachment.name}.${tmpAttachment.ext}`,
                                                fileExtension: tmpAttachment.ext,
                                            }
                                        })
                                    )
                                }
                                setChaptersList(
                                    data?.chapters?.length > 0
                                        ? data.chapters.map((chapter: Chapter) => {
                                              return {
                                                  ...chapter,
                                                  isDone: true,
                                                  isEdit: false,
                                                  attachments:
                                                      chapter?.attachments?.length > 0
                                                          ? chapter.attachments.map(
                                                                (a: AttachmentProps) => {
                                                                    return {
                                                                        ...a,
                                                                        fileUrl: a.url,
                                                                        fileName: `${a.name}.${a.ext}`,
                                                                        fileExtension: a.ext,
                                                                    }
                                                                }
                                                            )
                                                          : [],
                                              }
                                          })
                                        : []
                                )
                                setRequiredConfirmReading(data?.requiredConfirmReading)
                                setEnableCommentsAndReactions(data?.enableCommentsAndReactions)
                                setCustomStoreList(data?.parentFolder.storeList)
                                setReferenceFolderObject(data?.parentFolder)
                            })
                            break
                        case ResourceType.form:
                            getDocumentDetails(resourceId, (data: ResourceProps) => {
                                setFormTitle(data?.title)
                                setRecipients(data?.recipients || [])
                                setVisibility(data?.visibility || [])
                                setDepartment(data?.department || '')
                                setSelectedForms(data?.survey ? [data.survey] : [])
                                setStartDate(
                                    data?.survey?.startDate ? new Date(data.survey.startDate) : null
                                )
                                setEndDate(
                                    data?.survey?.endDate ? new Date(data.survey.endDate) : null
                                )
                                setPriority(data?.survey?.priority === 'urgent' ? true : false)
                                setCustomStoreList(data?.parentFolder.storeList)
                                setReferenceFolderObject({ ...data?.parentFolder })
                            })
                            break
                        default:
                            break
                    }
                }
            }
            if (externalReferenceFolderId && !resourceId) {
                ResourcesService.GetFolder({
                    folderId: externalReferenceFolderId,
                })
                    .then((res: any) => {
                        if (res && res.data) {
                            setCustomStoreList(res.data.storeList)
                            setReferenceFolderObject(res.data)
                        }
                    })
                    .finally(() => setFullLoading(false))
            }
        }
    }, [accessToken, resourceId, externalResourceType, externalReferenceFolderId])

    useEffect(() => {
        // if (isEdit) {
        //     setFullLoading(loading)
        // } else {
        //     setSmartFilter({})
        // }
        if (!isEdit) setSmartFilter({})
    }, [isEdit, loading])

    useEffect(() => {
        if (isEdit && smartFilter && Object.keys(smartFilter).length > 0) {
            mappingSmartFiltersChanges(compareStructures(smartFilter, originalSmartFilters))
        }
    }, [isEdit, smartFilter])

    useEffect(() => {
        if (resourceId) {
            switch (resourceType) {
                case ResourceType.file:
                case ResourceType.article:
                case ResourceType.form:
                    getDocumentDetails(resourceId, (data) => {
                        if (data.storeList && data.storeList.length > 0) {
                            setDistributionList((prev) => ({
                                ...prev,
                                selectedStoreList: {
                                    ...prev.selectedStoreList,
                                    value: data.storeList.map((s) => ({ store_id: s })),
                                },
                            }))
                        }
                    })
                    break
                default:
                    break
            }
        }
    }, [resourceId, resourceType])

    // FUNCTIONS

    const compareStructures = (structure1, structure2) => {
        const keys1 = Object?.keys(structure1)
        const keys2 = Object?.keys(structure2)

        if (keys1.length !== keys2.length) {
            return true
        }

        for (const key of keys1) {
            if (!keys2.includes(key)) {
                return true
            }

            const value1 = structure1[key]
            const value2 = structure2[key]

            if (Array.isArray(value1) && Array.isArray(value2)) {
                if (value1.length !== value2.length || !value1.every((v, i) => v === value2[i])) {
                    return true
                }
            } else if (value1 !== value2) {
                return true
            }
        }

        return false
    }

    const getDifference = (array1, array2) => {
        return (
            array1?.length === array2?.length &&
            array1.every((element_1) =>
                array2.some((element_2) => element_2.store_id === element_1.store_id)
            )
        )
    }

    const mappingSmartFiltersChanges = (smartFiltersChanges: boolean) => {
        if (smartFiltersChanges) {
            setStatus('modals.dialog.description.propagate')
        }
    }

    const mappingChanges = (newList: any) => {
        let add: boolean = false
        let remove: boolean = false
        let tmp: any

        let noChanges: boolean = getDifference(newList, originalStoreList)

        if (noChanges) {
            tmp = {
                previousStoreList: distributionList.selectedStoreList.value,
                actualStoreList: newList,
                add: false,
                remove: false,
            }
        } else if (!noChanges) {
            if (distributionList.selectedStoreList.value?.length > newList?.length) {
                remove = true
            } else if (distributionList.selectedStoreList.value?.length < newList?.length) {
                add = true
            }

            tmp = {
                previousStoreList: distributionList.selectedStoreList.value,
                actualStoreList: newList,
                add: matchingTable?.add ? matchingTable.add : add,
                remove: matchingTable?.remove ? matchingTable.remove : remove,
            }
        }

        setStatus &&
            setStatus(
                tmp.add && tmp.remove
                    ? 'modals.dialog.description.propagate'
                    : tmp.add
                    ? 'modals.dialog.description.propagate'
                    : tmp.remove
                    ? 'modals.dialog.description.propagate.remove'
                    : 'no-changes'
            )

        setMatchingTable(tmp)
    }

    const onAddChapter = (whichChapter: Chapter) => {
        setChaptersList([...chaptersList, whichChapter])
    }

    const onSaveChapter = (chapterIndex: number, whichChapter: Chapter) => {
        let tmpChaptersList = [...chaptersList]
        tmpChaptersList[chapterIndex] = { ...whichChapter }
        setChaptersList(tmpChaptersList)
    }

    const onEditChapter = (chapterIndex: number, whichChapter: Chapter) => {
        let tmpChaptersList = [...chaptersList]
        tmpChaptersList[chapterIndex] = { ...whichChapter }
        setChaptersList(tmpChaptersList)
    }

    const onRemoveChapter = (chapterIndex: number) => {
        let tmpChaptersList = [...chaptersList]
        tmpChaptersList.splice(chapterIndex, 1)
        setChaptersList(tmpChaptersList)
    }

    const onReduceChapter = (chapterIndex: number, whichChapter: Chapter) => {
        if (whichChapter.isEdit) {
            let tmpChaptersList = [...chaptersList]
            tmpChaptersList[chapterIndex] = { ...whichChapter, isEdit: false }
            setChaptersList(tmpChaptersList)
        }
        onRemoveChapter(chapterIndex)
    }

    const getVisibilitySection = () => {
        return (
            <div className={`${componentClass}__visibility ${isEdit ? 'disabled' : ''}`}>
                <LuxMultiSelect
                    mandatory={true}
                    value={visibility}
                    onChangeCallback={(v: any) => setVisibility(v)}
                    label={intl.formatMessage({
                        id: 'modals.new_resource.visibility',
                        defaultMessage: 'Visibility',
                    })}
                    placeholder={intl.formatMessage({
                        id: 'modals.new_resource.visibility.placeholder',
                        defaultMessage: 'Select role',
                    })}
                    disabled={
                        (resourceType !== ResourceType.folder &&
                            resourceType !== ResourceType.subfolder &&
                            resourceType !== ResourceType.file &&
                            resourceType !== ResourceType.article &&
                            resourceType !== ResourceType.form) ||
                        ((resourceType === ResourceType.subfolder ||
                            resourceType === ResourceType.file ||
                            resourceType === ResourceType.article ||
                            resourceType === ResourceType.form) &&
                            referenceFolderObject._id === '')
                    }
                    options={
                        referenceFolderObject?.visibility?.length > 0 &&
                        (resourceType === ResourceType.subfolder ||
                            resourceType === ResourceType.file ||
                            resourceType === ResourceType.article ||
                            resourceType === ResourceType.form)
                            ? referenceFolderObject?.visibility.map((visibility: string) => ({
                                  value: visibility,
                                  label: intl.formatMessage({
                                      id: `modals.filters.visibility.${visibility}`,
                                      defaultMessage: visibility,
                                  }),
                              }))
                            : visibilityList.map(
                                  (visibility: { value: string; label: string }) => ({
                                      ...visibility,
                                      label: intl.formatMessage({
                                          id: visibility.label,
                                          defaultMessage: visibility.label.split('.').pop(),
                                      }),
                                  })
                              )
                    }
                    additionalProps={{
                        appendTo: 'self',
                        readOnly:
                            isEdit ||
                            (resourceType !== ResourceType.folder &&
                                resourceType !== ResourceType.subfolder &&
                                resourceType !== ResourceType.file &&
                                resourceType !== ResourceType.article &&
                                resourceType !== ResourceType.form) ||
                            ((resourceType === ResourceType.subfolder ||
                                resourceType === ResourceType.file ||
                                resourceType === ResourceType.article ||
                                resourceType === ResourceType.form) &&
                                referenceFolderObject._id === ''),
                    }}
                />
            </div>
        )
    }

    const loadMoreFolders = (treeFolder: TreeFolderProps) => {
        let tmpFolders = [...folders]
        ResourcesService.GetResources({ folderId: treeFolder._id }).then((res: any) => {
            if (res && res.data && res.data.data) {
                if (res.data.data?.length === 1) {
                    let subfolders = res.data.data[0].folders
                    let _id = treeFolder._id
                    let newFolders = tmpFolders.map((f: TreeFolderProps) => {
                        if (f._id === _id) {
                            return {
                                ...f,
                                folders: subfolders,
                            }
                        }
                        return { ...f }
                    })
                    setFolders(newFolders)
                }
            }
        })
    }

    const accordionTabs: AccordionTabProps[] = [
        {
            title: intl.formatMessage({
                id: 'modals.new_resource.accordion_tab.title.reference_folder',
                defaultMessage: 'Reference Folder',
            }),
            content: (
                <ReferenceFolder
                    loading={loading}
                    folders={folders}
                    resourceType={resourceType}
                    referenceFolderId={referenceFolderId}
                    referenceFolderObject={referenceFolderObject}
                    setFullLoading={setFullLoading}
                    loadingFolders={loadingFolders}
                    loadMoreFolders={loadMoreFolders}
                    setReferenceFolderId={setReferenceFolderId}
                    setReferenceFolderObject={setReferenceFolderObject}
                    setFoldersDetails={setFolderDetails}
                    onChangeFolder={onChangeParentFolder}
                />
            ),
            disabled: resourceType === ResourceType.folder,
        },
        {
            title: intl.formatMessage({
                id: 'modals.new_resource.accordion_tab.title.information',
                defaultMessage: 'Information',
            }),
            content: (
                <Information
                    isEdit={isEdit}
                    resourceType={resourceType}
                    folderDetails={folderDetails}
                    departmentList={departmentList}
                    referenceFolderObject={referenceFolderObject}
                    folderTitle={folderTitle}
                    setFolderTitle={setFolderTitle}
                    folderType={folderType}
                    setFolderType={setFolderType}
                    department={department}
                    setDepartment={setDepartment}
                    recipients={recipients}
                    setRecipients={setRecipients}
                    articleTitle={articleTitle}
                    setArticleTitle={setArticleTitle}
                    formTitle={formTitle}
                    setFormTitle={setFormTitle}
                    selectedForms={selectedForms}
                    setSelectedForms={setSelectedForms}
                    abstract={abstract}
                    setAbstract={setAbstract}
                    attachments={attachments}
                    setAttachments={setAttachments}
                    resetViewsCount={resetViewsCount}
                    setResetViewsCount={setResetViewsCount}
                    requiredConfirmReading={requiredConfirmReading}
                    setRequiredConfirmReading={setRequiredConfirmReading}
                    enableCommentsAndReactions={enableCommentsAndReactions}
                    setEnableCommentsAndReactions={setEnableCommentsAndReactions}
                    showResetViewsCount={showResetViewsCount}
                    setShowResetViewsCount={setShowResetViewsCount}
                />
            ),
            disabled: false,
        },
        {
            title: intl.formatMessage({
                id: 'modals.new_resource.label.article_contents',
                defaultMessage: 'Article Contents',
            }),
            content: (
                <ArticleChapters
                    chaptersList={chaptersList}
                    onAddChapter={onAddChapter}
                    onSaveChapter={onSaveChapter}
                    onEditChapter={onEditChapter}
                    onRemoveChapter={onRemoveChapter}
                    onReduceChapter={onReduceChapter}
                />
            ),
            disabled: resourceType !== ResourceType.article,
        },
        {
            id: 'distribution',
            title: intl.formatMessage({
                id: 'modals.new.activity.section.distribution',
                defaultMessage: 'Distribution',
            }),
            content: (
                <Distribution
                    tableConfigId={`resources_${resourceType}`}
                    form={distributionList}
                    onSetForm={(form: any) => {
                        setDistributionList((value: any) => ({
                            ...value,
                            ...form,
                        }))
                    }}
                    onSave={() => null}
                    disableHeaderActions={false}
                    customStoreList={customStoreList}
                    afterTableTemplate={getVisibilitySection()}
                    isReadOnly={
                        (resourceType !== ResourceType.folder &&
                            resourceType !== ResourceType.subfolder &&
                            resourceType !== ResourceType.file &&
                            resourceType !== ResourceType.article &&
                            resourceType !== ResourceType.form) ||
                        ((resourceType === ResourceType.subfolder ||
                            resourceType === ResourceType.file ||
                            resourceType === ResourceType.article ||
                            resourceType === ResourceType.form) &&
                            referenceFolderObject._id === '')
                    }
                    disableSelection={
                        (resourceType !== ResourceType.folder &&
                            resourceType !== ResourceType.subfolder &&
                            resourceType !== ResourceType.file &&
                            resourceType !== ResourceType.article &&
                            resourceType !== ResourceType.form) ||
                        ((resourceType === ResourceType.subfolder ||
                            resourceType === ResourceType.file ||
                            resourceType === ResourceType.article ||
                            resourceType === ResourceType.form) &&
                            referenceFolderObject._id === '')
                    }
                    showDialogPropagation={showDialogPropagation}
                    setShowDialogPropagation={setShowDialogPropagation}
                    isEditResources={isEdit}
                    smartFilter={smartFilter}
                    setSmartFilter={setSmartFilter}
                    setOriginalStoreList={setOriginalStoreList}
                    mappingChanges={mappingChanges}
                />
            ),
            disabled: false,
        },
        {
            title: intl.formatMessage({
                id: 'modals.new.activity.section.scheduling',
                defaultMessage: 'Scheduling',
            }),
            content: (
                <Scheduling
                    isEdit={isEdit}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    priority={priority}
                    setPriority={setPriority}
                />
            ),
            disabled: resourceType !== ResourceType.form,
        },
    ]

    const breadcrumbs: MenuItem[] = [
        {
            label: intl.formatMessage({
                id: 'modals.new_resource.breadcrumbs.resources',
                defaultMessage: 'Resources',
            }),
        },
        {
            label: intl.formatMessage({
                id:
                    resourceType === ResourceType.folder ||
                    resourceType === ResourceType.subfolder ||
                    resourceType === ResourceType.file ||
                    resourceType === ResourceType.article ||
                    resourceType === ResourceType.form
                        ? isEdit
                            ? `modals.new_resource.label.${resourceType}_editing`
                            : `modals.new_resource.breadcrumbs.create_new_${resourceType}`
                        : isEdit
                        ? 'modals.new_resource.label.document_editing'
                        : 'modals.new_resource.breadcrumbs.create_new_document',
                defaultMessage:
                    resourceType === ResourceType.folder ||
                    resourceType === ResourceType.subfolder ||
                    resourceType === ResourceType.file ||
                    resourceType === ResourceType.article ||
                    resourceType === ResourceType.form
                        ? isEdit
                            ? `${resourceType} editing`
                            : `New ${resourceType} creation`
                        : isEdit
                        ? 'Document editing'
                        : 'New document creation',
            }),
            disabled: true,
        },
    ]

    const resourcesTypes: { name: ResourceType; icon: string; disabled?: boolean }[] = [
        {
            name: ResourceType.file,
            icon: 'file',
            disabled:
                !canCreate(SmartlyActionItems.DOCUMENT) ||
                (isEdit && resourceType !== ResourceType.file) ||
                (!isEdit && externalResourceType === ResourceType.form) ||
                (!isEdit &&
                    !!referenceFolderObject?.folderType &&
                    referenceFolderObject?.folderType === FolderType.form),
        },
        {
            name: ResourceType.article,
            icon: 'document',
            disabled:
                !canCreate(SmartlyActionItems.DOCUMENT) ||
                (isEdit && resourceType !== ResourceType.article) ||
                (!isEdit && externalResourceType === ResourceType.form) ||
                (!isEdit &&
                    !!referenceFolderObject?.folderType &&
                    referenceFolderObject?.folderType === FolderType.form),
        },
        {
            name: ResourceType.form,
            icon: 'list',
            disabled:
                !canCreate(SmartlyActionItems.FORM) ||
                (isEdit && resourceType !== ResourceType.form) ||
                (!isEdit &&
                    !!referenceFolderObject?.folderType &&
                    referenceFolderObject?.folderType !== FolderType.form) ||
                (!isEdit && resourceType === ResourceType.document) ||
                (!isEdit &&
                    externalResourceType === ResourceType.file &&
                    window.location.pathname.includes('/resources/documents')),
        },
    ]

    let gatekeepers = [Visibility.gatekeeper, Visibility.gatekeeperadmin]
    let creators = [
        Visibility.creator,
        Visibility.picturemanager,
        Visibility.picturemanageradmin,
        Visibility.virtualstore,
    ]

    const expandedVisibility = () => {
        let newVisibiliy = []
        let tmpVisibility = [...visibility]
        if (tmpVisibility?.length > 0) {
            for (let i = 0; i < tmpVisibility.length; i++) {
                let v = tmpVisibility[i]
                switch (v) {
                    case Visibility.gatekeeper:
                    case Visibility.gatekeeperadmin:
                        if (!newVisibiliy.includes(v)) {
                            newVisibiliy.push(v)
                        }
                        break
                    case Visibility.creator:
                    case Visibility.picturemanageradmin:
                    case Visibility.picturemanager:
                    case Visibility.virtualstore:
                        gatekeepers.forEach((g) => {
                            if (!newVisibiliy.includes(g)) {
                                newVisibiliy.push(g)
                            }
                        })
                        if (!newVisibiliy.includes(v)) {
                            newVisibiliy.push(v)
                        }
                        break
                    case Visibility.regionalmanager:
                        gatekeepers.concat(creators).forEach((gc) => {
                            if (!newVisibiliy.includes(gc)) {
                                newVisibiliy.push(gc)
                            }
                        })
                        if (!newVisibiliy.includes(v)) {
                            newVisibiliy.push(v)
                        }
                        break
                    case Visibility.storemanager:
                        gatekeepers
                            .concat(creators)
                            .concat([Visibility.regionalmanager])
                            .forEach((gc) => {
                                if (!newVisibiliy.includes(gc)) {
                                    newVisibiliy.push(gc)
                                }
                            })
                        if (!newVisibiliy.includes(v)) {
                            newVisibiliy.push(v)
                        }
                        break
                    case Visibility.storeassociate:
                    case Visibility.optemployeed:
                    case Visibility.optindependent:
                        gatekeepers
                            .concat(creators)
                            .concat([Visibility.regionalmanager, Visibility.storemanager])
                            .forEach((gc) => {
                                if (!newVisibiliy.includes(gc)) {
                                    newVisibiliy.push(gc)
                                }
                            })
                        if (!newVisibiliy.includes(v)) {
                            newVisibiliy.push(v)
                        }
                        break
                    default:
                        break
                }
            }
        }
        return newVisibiliy
    }

    const onSave = (notPropagate?: boolean) => {
        switch (resourceType) {
            case ResourceType.folder:
                isEdit
                    ? editFolder({
                          title: folderTitle,
                          folderId: resourceId,
                          force: !notPropagate ? false : propagate,
                          visibility: expandedVisibility(),
                          storeList: distributionList.selectedStoreList.value.map((s: any) => {
                              return s.store_id
                          }),
                          smartFilter:
                              smartFilter && Object.keys(smartFilter).length > 0
                                  ? smartFilter
                                  : null,
                      })
                    : createFolder({
                          title: folderTitle,
                          folderType: folderType,
                          smartFilter: Object.keys(smartFilter).length > 0 ? smartFilter : null,
                          visibility: expandedVisibility(),
                          storeList: distributionList.selectedStoreList.value.map((s: any) => {
                              return s.store_id
                          }),
                      })
                break
            case ResourceType.subfolder:
                isEdit
                    ? editFolder({
                          title: folderTitle,
                          folderId: resourceId,
                          force: !notPropagate ? false : propagate,
                          visibility,
                          parentFolderId: referenceFolderId,
                          storeList: distributionList.selectedStoreList.value.map((s: any) => {
                              return s.store_id
                          }),
                          smartFilter:
                              smartFilter && Object.keys(smartFilter).length > 0
                                  ? smartFilter
                                  : null,
                      })
                    : createFolder({
                          title: folderTitle,
                          smartFilter: Object.keys(smartFilter).length > 0 ? smartFilter : null,
                          visibility,
                          parentFolderId: referenceFolderObject?._id,
                          folderType: referenceFolderObject?.folderType,
                          storeList: distributionList.selectedStoreList.value.map((s: any) => {
                              return s.store_id
                          }),
                      })
                break
            case ResourceType.file:
                isEdit && resourceId
                    ? editDocument(resourceId, {
                          title: attachments[0]?.fileName,
                          department: department,
                          attachments: attachments.map((file) => ({
                              _id: file?._id,
                          })),
                          parentFolderId: referenceFolderObject?._id,
                          storeList: distributionList.selectedStoreList.value.map((s: any) => {
                              return s.store_id
                          }),
                          visibility,
                      })
                    : createDocument({
                          resourceType: ResourceType.file,
                          department: department,
                          attachmentData: attachments.map((file) => ({
                              title: file?.fileName,
                              _id: file?._id,
                          })),
                          parentFolderId: referenceFolderObject?._id,
                          storeList: distributionList.selectedStoreList.value.map((s: any) => {
                              return s.store_id
                          }),
                          visibility,
                      })
                break
            case ResourceType.article:
                isEdit && resourceId
                    ? editDocument(resourceId, {
                          title: articleTitle,
                          department: department,
                          abstract: abstract,
                          chapters: chaptersList.map((c: Chapter) => {
                              return {
                                  text: c.text,
                                  title: c.title,
                                  index: c.index,
                                  attachments:
                                      c.attachments?.length > 0
                                          ? c.attachments.map((a: AttachmentProps) => {
                                                return { _id: a._id }
                                            })
                                          : [],
                              }
                          }),
                          attachments:
                              attachments?.length > 0
                                  ? attachments.map((a: AttachmentProps) => {
                                        return { _id: a._id }
                                    })
                                  : [],
                          resetViewsCount: resetViewsCount,
                          requiredConfirmReading: requiredConfirmReading,
                          enableCommentsAndReactions: enableCommentsAndReactions,
                          parentFolderId: referenceFolderObject?._id,
                          storeList: distributionList.selectedStoreList.value.map((s: any) => {
                              return s.store_id
                          }),
                          visibility,
                      })
                    : createDocument({
                          title: articleTitle,
                          abstract: abstract,
                          department: department,
                          chapters: chaptersList.map((c: Chapter) => {
                              return {
                                  text: c.text,
                                  title: c.title,
                                  index: c.index,
                                  attachments:
                                      c.attachments?.length > 0
                                          ? c.attachments.map((a: AttachmentProps) => {
                                                return { _id: a._id }
                                            })
                                          : [],
                              }
                          }),
                          attachments:
                              attachments?.length > 0
                                  ? attachments.map((a: AttachmentProps) => {
                                        return { _id: a._id }
                                    })
                                  : [],
                          resourceType: ResourceType.article,
                          parentFolderId: referenceFolderObject?._id,
                          requiredConfirmReading: requiredConfirmReading,
                          enableCommentsAndReactions: enableCommentsAndReactions,
                          storeList: distributionList.selectedStoreList.value.map((s: any) => {
                              return s.store_id
                          }),
                          visibility,
                      })
                break
            case ResourceType.form:
                isEdit && resourceId
                    ? editDocument(resourceId, {
                          title: formTitle,
                          department: department,
                          recipients: recipients,
                          parentFolderId: referenceFolderObject?._id,
                          survey: {
                              surveyId: selectedForms?.length > 0 ? selectedForms[0]?.surveyId : '',
                              startDate: new Date(startDate).getTime(),
                              endDate: new Date(endDate).getTime(),
                              priority: priority ? 'urgent' : 'standard',
                          },
                          storeList: distributionList.selectedStoreList.value.map((s: any) => {
                              return s.store_id
                          }),
                          visibility,
                      })
                    : createDocument({
                          title: formTitle,
                          resourceType: ResourceType.form,
                          department: department,
                          parentFolderId: referenceFolderObject?._id,
                          recipients: recipients,
                          survey: {
                              surveyId: selectedForms?.length > 0 ? selectedForms[0]?.surveyId : '',
                              startDate: new Date(startDate).getTime(),
                              endDate: new Date(endDate).getTime(),
                              priority: priority ? 'urgent' : 'standard',
                          },
                          storeList: distributionList.selectedStoreList.value.map((s: any) => {
                              return s.store_id
                          }),
                          visibility,
                      })
                break
            default:
                break
        }
    }

    const onPublish = () => {
        isEdit && resourceId
            ? editDocument(
                  resourceId,
                  {
                      title: articleTitle,
                      department: department,
                      abstract: abstract,
                      chapters: chaptersList.map((c: Chapter) => {
                          return {
                              text: c.text,
                              title: c.title,
                              index: c.index,
                              attachments:
                                  c.attachments?.length > 0
                                      ? c.attachments.map((a: AttachmentProps) => {
                                            return { _id: a._id }
                                        })
                                      : [],
                          }
                      }),
                      attachments:
                          attachments?.length > 0
                              ? attachments.map((a: AttachmentProps) => {
                                    return { _id: a._id }
                                })
                              : [],
                      resetViewsCount: resetViewsCount,
                      requiredConfirmReading: requiredConfirmReading,
                      enableCommentsAndReactions: enableCommentsAndReactions,
                      parentFolderId: referenceFolderObject?._id,
                      storeList: distributionList.selectedStoreList.value.map((s: any) => {
                          return s.store_id
                      }),
                      visibility,
                  },
                  true
              )
            : createDocument(
                  {
                      title: articleTitle,
                      abstract: abstract,
                      department: department,
                      chapters: chaptersList.map((c: Chapter) => {
                          return {
                              text: c.text,
                              title: c.title,
                              index: c.index,
                              attachments:
                                  c.attachments?.length > 0
                                      ? c.attachments.map((a: AttachmentProps) => {
                                            return { _id: a._id }
                                        })
                                      : [],
                          }
                      }),
                      attachments:
                          attachments?.length > 0
                              ? attachments.map((a: AttachmentProps) => {
                                    return { _id: a._id }
                                })
                              : [],
                      resourceType: ResourceType.article,
                      parentFolderId: referenceFolderObject?._id,
                      requiredConfirmReading: requiredConfirmReading,
                      enableCommentsAndReactions: enableCommentsAndReactions,
                      storeList: distributionList.selectedStoreList.value.map((s: any) => {
                          return s.store_id
                      }),
                      visibility,
                  },
                  true
              )
    }

    const footer = (
        <div className={`${componentClass}__footer`}>
            {resourceType === ResourceType.article ? (
                <>
                    <LuxButton
                        variant="text"
                        onClick={onSave}
                        disabled={saveDisabled}
                        text={intl.formatMessage({
                            id: 'modals.new.activity.creation.cta.save.as.draft',
                            defaultMessage: 'Save as Draft',
                        })}
                    />
                    <LuxButton
                        onClick={onPublish}
                        disabled={saveDisabled}
                        text={intl.formatMessage({
                            id: 'modals.dialog.buttons.save',
                            defaultMessage: 'Save',
                        })}
                    />
                </>
            ) : (
                <>
                    <div></div>
                    <LuxButton
                        onClick={() => {
                            {
                                if (
                                    status.includes('no-changes') ||
                                    folderDetails.countSubFolders === 0 ||
                                    (resourceType !== ResourceType.folder &&
                                        resourceType !== ResourceType.subfolder)
                                ) {
                                    onSave()
                                } else {
                                    setShowDialogPropagation(true)
                                }
                            }
                        }}
                        disabled={saveDisabled}
                        text={intl.formatMessage({
                            id: 'modals.dialog.buttons.save',
                            defaultMessage: 'Save',
                        })}
                    />
                </>
            )}
        </div>
    )

    return (
        <div className={componentClass}>
            <LuxModalFullScreen
                footer={footer}
                onClose={onClose}
                hideSidebar={true}
                breadcrumbMenu={breadcrumbs}
                labelHeaderMobile={intl.formatMessage({
                    id: isEdit
                        ? 'modals.new_resource.breadcrumbs.edit_resource'
                        : 'modals.new_resource.breadcrumbs.create_new_resource',
                    defaultMessage: isEdit ? 'Edit resource' : 'New resource creation',
                })}>
                <div className={`${componentClass}__content`}>
                    {externalResourceType === ResourceType.folder ||
                    externalResourceType === ResourceType.subfolder ? (
                        <div
                            style={{ marginBottom: 40 }}
                            className={`${componentClass}__content__title`}>
                            {intl.formatMessage({
                                id: isEdit
                                    ? `modals.new_resource.label.${externalResourceType}_editing`
                                    : `modals.new_resource.label.new_${externalResourceType}_creation`,
                                defaultMessage: isEdit
                                    ? `${externalResourceType} editing`
                                    : `New ${externalResourceType} creation`,
                            })}
                        </div>
                    ) : (
                        <>
                            <div className={`${componentClass}__content__title`}>
                                {isEdit
                                    ? intl.formatMessage({
                                          id: 'modals.new_resource.label.document_editing',
                                          defaultMessage: 'Document editing',
                                      })
                                    : intl.formatMessage({
                                          id: 'modals.new_resource.label.document_type',
                                          defaultMessage: 'Document Type',
                                      })}
                            </div>
                            <div className={`${componentClass}__content__type`}>
                                {resourcesTypes.map(
                                    (
                                        t: { name: ResourceType; icon: string; disabled?: boolean },
                                        i: number
                                    ) => {
                                        return (
                                            <LuxButton
                                                key={i}
                                                isTag={true}
                                                text={intl.formatMessage({
                                                    id: `modals.new_resource.folder_type.${t.name}`,
                                                    defaultMessage: t.name,
                                                })}
                                                iconLeft={t.icon}
                                                iconRight={
                                                    resourceType === t.name ? 'status-ok' : ''
                                                }
                                                color={
                                                    resourceType === t.name
                                                        ? Color.BLUE
                                                        : Color.BG_GREY
                                                }
                                                style={{
                                                    backgroundColor:
                                                        resourceType === t.name
                                                            ? 'var(--lux-primary)'
                                                            : 'var(--lux-bg-grey)',
                                                    color:
                                                        resourceType === t.name
                                                            ? 'var(--lux-white)'
                                                            : 'var(--lux-black)',
                                                }}
                                                onClick={() => {
                                                    if (t.name === ResourceType.form)
                                                        Analytics._sendAnalyticsLinkEvent(
                                                            'Resources',
                                                            'Forms:Creation',
                                                            'Static'
                                                        )
                                                    setResourceType(t.name)
                                                    setReferenceFolderObject({
                                                        _id: '',
                                                        title: '',
                                                    })
                                                }}
                                                disabled={t.disabled}
                                            />
                                        )
                                    }
                                )}
                            </div>
                        </>
                    )}
                    <div className={`${componentClass}__content__folderType`}>
                        {isEdit && (
                            <LuxTextInput
                                disabled={true}
                                mandatory={false}
                                onChangeCallback={() => null}
                                additionalProps={{ readOnly: true }}
                                value={
                                    resourceType === ResourceType.folder && folderType
                                        ? intl.formatMessage({
                                              id: `modals.new_resource.folder_type.${folderType}`,
                                              defaultMessage: folderType,
                                          })
                                        : resourceType === ResourceType.subfolder &&
                                          referenceFolderObject?.folderType
                                        ? intl.formatMessage({
                                              id: `modals.new_resource.folder_type.${referenceFolderObject.folderType}`,
                                              defaultMessage: referenceFolderObject.folderType,
                                          })
                                        : resourceType === ResourceType.file ||
                                          resourceType === ResourceType.article
                                        ? intl.formatMessage({
                                              id: `modals.new_resource.folder_type.${FolderType.document}`,
                                              defaultMessage: FolderType.document,
                                          })
                                        : resourceType === ResourceType.form
                                        ? intl.formatMessage({
                                              id: `modals.new_resource.folder_type.${FolderType.form}`,
                                              defaultMessage: FolderType.form,
                                          })
                                        : ''
                                }
                                label={intl.formatMessage({
                                    id: 'modals.new_resource.label.folder_type',
                                    defaultMessage: 'Folder Type',
                                })}
                                placeholder={intl.formatMessage({
                                    id: 'modals.new_resource.label.select_type',
                                    defaultMessage: 'Select type',
                                })}
                            />
                        )}
                        {!isEdit && (
                            <LuxSelect
                                mandatory={false}
                                value={
                                    resourceType === ResourceType.subfolder
                                        ? referenceSpace
                                        : folderType || ResourceType.document
                                }
                                disabled={
                                    resourceType !== ResourceType.folder &&
                                    resourceType !== ResourceType.subfolder
                                }
                                onChangeCallback={(e: any) => {
                                    if (resourceType === ResourceType.subfolder) {
                                        setReferenceSpace(e)
                                    } else {
                                        setFolderType(e)
                                    }
                                }}
                                label={intl.formatMessage({
                                    id: 'modals.new_resource.label.folder_type',
                                    defaultMessage: 'Folder Type',
                                })}
                                placeholder={intl.formatMessage({
                                    id: 'modals.new_resource.label.select_type',
                                    defaultMessage: 'Select type',
                                })}
                                options={[
                                    {
                                        value: FolderType.document,
                                        label: `${intl.formatMessage({
                                            id: `modals.new_resource.folder_type.${FolderType.document}`,
                                            defaultMessage: FolderType.document,
                                        })}`,
                                        disabled: !canCreate(SmartlyActionItems.DOCUMENT),
                                    },
                                    {
                                        value: FolderType.form,
                                        label: `${intl.formatMessage({
                                            id: `modals.new_resource.folder_type.${FolderType.form}`,
                                            defaultMessage: FolderType.form,
                                        })}`,
                                        disabled: !canCreate(SmartlyActionItems.FORM),
                                    },
                                ]}
                            />
                        )}
                    </div>
                    <div className={`${componentClass}__content__accordions`}>
                        {accordionTabs?.length > 0 &&
                            accordionTabs
                                ?.filter((t: AccordionTabProps) => {
                                    return !t.disabled
                                })
                                ?.map((t: AccordionTabProps, i: number) => {
                                    return (
                                        <LuxAccordion key={i} title={t.title} activeIndex={i}>
                                            {t.content}
                                        </LuxAccordion>
                                    )
                                })}
                    </div>
                </div>
            </LuxModalFullScreen>
            <DialogPropagation
                dialogVisible={showDialogPropagation}
                onSave={onSave}
                onHide={() => {
                    setShowDialogPropagation(false)
                }}
                status={status}
            />
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    accessToken: state.userReducer.accessToken,
    documents: state[NEW_RESOURCE_REDUCER_NAMESPACE].documents,
    forms: state[NEW_RESOURCE_REDUCER_NAMESPACE].forms,
    loading: state[NEW_RESOURCE_REDUCER_NAMESPACE].loading,
    loadingFolders: state[NEW_RESOURCE_REDUCER_NAMESPACE].loadingFolders,
    departmentList: state[FILTERS_REDUCER_NAMESPACE].departmentList,
    visibilityList: state[FILTERS_REDUCER_NAMESPACE].visibilityList,
    resourceId: state.layoutReducer.modals.props?.id,
    isOpenDocument: state.layoutReducer.modals.props?.isOpenDocument,
    isFormSection: state.layoutReducer.modals.props?.isFormSection,
    externalResourceType: state.layoutReducer.modals.props?.resourceType,
    externalReferenceFolderId: state.layoutReducer.modals.props?.referenceFolderId,
    externalReferenceFolderTitle: state.layoutReducer.modals.props?.referenceFolderTitle,
    smartFilter: state[NEW_RESOURCE_REDUCER_NAMESPACE].smartFilter,
    documentsApiFullfilled: state[NEW_RESOURCE_REDUCER_NAMESPACE].documentsApiFullfilled,
})

const mapDispatchToProps = (dispatch: any) => ({
    getDocuments: (filters: { folderType: FolderType }) => dispatch(getDocuments(filters)),
    getForms: (filters: { folderType: FolderType }) => dispatch(getForms(filters)),
    getDepartments: () => {
        dispatch(getDepartments())
    },
    getVisibilities: () => {
        dispatch(getVisibilities())
    },
    getDocumentDetails: (documentId: string, callback: (data: ResourceProps) => void) =>
        dispatch(getDocumentDetails(documentId, callback)),
    createFolder: (body: {
        title: string
        folderType: string
        visibility: Visibility[]
        storeList: string[]
        parentFolderId?: string
    }) => dispatch(createFolder(body)),
    editFolder: (body: {
        title: string
        visibility: Visibility[]
        storeList: string[]
        folderId: string
        parentFolderId?: string
        force?: boolean
    }) => dispatch(editFolder(body)),
    createDocument: (
        body: {
            title: string
            resourceType: string
            department: string
            attachments?: AttachmentProps[]
            parentFolderId?: string
            recipients?: string[]
            survey?: SurveyProps
            abstract?: any
            chapters?: Chapter[]
            requiredConfirmReading?: boolean
            enableCommentsAndReactions?: boolean
            storeList: string[]
            visibility: Visibility[]
        },
        publishAfterSave?: boolean
    ) => dispatch(createDocument(body, publishAfterSave)),
    editDocument: (
        documentId: string,
        body: {
            title: string
            department: string
            attachments?: AttachmentProps[]
            parentFolderId?: string
            recipients?: string[]
            survey?: SurveyProps
            abstract?: any
            chapters?: Chapter[]
            resetViewsCount?: boolean
            requiredConfirmReading?: boolean
            enableCommentsAndReactions?: boolean
            storeList: string[]
            visibility: Visibility[]
        },
        publishAfterSave?: boolean
    ) => dispatch(editDocument(documentId, body, publishAfterSave)),
    onClose: () => {
        dispatch(
            layoutActions.setModals({
                type: null,
                props: {
                    id: null,
                    storeIdList: null,
                    isEdit: false,
                    isClone: false,
                    resourceType: null,
                    isOpenDocument: false,
                },
            })
        )
    },
    publishArticle: (documentId: string) => dispatch(publishArticle(documentId)),
    setFullLoading: (fullLoading: boolean) =>
        dispatch(layoutActions.setModals({ props: { loading: fullLoading } })),
    setSmartFilter: (value: any) => dispatch(setSmartFilterResources(value)),
    getFiltersDomain: () => dispatch(getFiltersDomain()),
})

export default connect(mapStateToProps, mapDispatchToProps)(NewResource)
